/**
 * Theme: light (default), dark, or follow the system. Persisted per browser
 * in localStorage; index.html applies it before first paint so there is no
 * flash. Tokens for both modes live in styles/globals.css.
 */
export type ThemePreference = 'light' | 'dark' | 'system'
const KEY = 'doable-theme'

export function getThemePreference(): ThemePreference {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'dark' || v === 'light' || v === 'system' ? v : 'system'
  } catch {
    return 'system'
  }
}

export function resolveTheme(pref: ThemePreference): 'light' | 'dark' {
  if (pref !== 'system') return pref
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function applyTheme(pref: ThemePreference): void {
  const resolved = resolveTheme(pref)
  const root = document.documentElement
  root.dataset.theme = resolved
  root.style.colorScheme = resolved
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', resolved === 'dark' ? '#09090b' : '#fafafa')
}

export function setThemePreference(pref: ThemePreference): void {
  try { localStorage.setItem(KEY, pref) } catch { /* private mode */ }
  applyTheme(pref)
  window.dispatchEvent(new CustomEvent('doable-theme', { detail: pref }))
}

/** Call once at boot: applies the stored preference and tracks system changes. */
export function initTheme(): void {
  applyTheme(getThemePreference())
  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (getThemePreference() === 'system') applyTheme('system')
  })
}
